import { type FormEvent, useId, useState } from "react";
import { api } from "./api.ts";
import { useJustDone } from "./justDone.ts";
import { linkIn } from "./linkIn.ts";
import { useKept, useOnline } from "./offline.ts";
import { ReportThis } from "./ReportThis.tsx";
import { Button } from "./souvenir.ts";

// Save a link on the web (DESIGN.md, Views, the web's own edits; features/links.md, "Save a link on the web"): a
// pasted address and a note if I like, into the trip's links inbox, saved as an AI saves one (its preview fetched
// the same way), for the AI to bring in when asked. One write signed "on the web", undoable at once (the trip's
// notice) and from Updates. Here on Ideas' links; a link shared to Tripmo from a phone comes by ShareLink.tsx.

const MAX_NOTE = 1000;

export function SaveLink({ trip, onChanged }: { trip: string; onChanged: () => void }) {
  const id = useId();
  const say = useJustDone();
  const online = useOnline();
  const kept = useKept();
  const offline = !online || kept !== null;
  const [url, setUrl] = useState("");
  const [note, setNote] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || offline) return;
    const link = linkIn(url);
    if (!link) {
      setProblem("Paste a link, like https://www.tiktok.com/@kyotofoodie/video/…");
      return;
    }
    setProblem(null);
    setError(null);
    setBusy(true);
    try {
      const { change } = await api.saveLink(trip, { url: link, ...(note.trim() ? { note: note.trim() } : {}) });
      setUrl("");
      setNote("");
      onChanged();
      say({ change, text: "Saved the link to the inbox" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save the link. Try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="tm-save-link" aria-label="Save a link" noValidate onSubmit={save}>
      <div className={`sv-field tm-booking-field${problem ? " is-wrong" : ""}`}>
        <label className="sv-field-label" htmlFor={`${id}-url`}>
          Save a link
        </label>
        <input
          id={`${id}-url`}
          className="sv-field-input"
          data-edit="save-link"
          type="url"
          inputMode="url"
          autoComplete="off"
          placeholder="Paste a link: a video, a post, a blog"
          value={url}
          aria-invalid={problem ? true : undefined}
          onChange={(e) => {
            setUrl(e.target.value);
            setProblem(null);
          }}
        />
        {problem && <span className="sv-field-hint tm-error">{problem}</span>}
      </div>
      {url.trim() && (
        <div className="sv-field tm-booking-field">
          <label className="sv-field-label" htmlFor={`${id}-note`}>
            A note, if you like
          </label>
          <input
            id={`${id}-note`}
            className="sv-field-input"
            autoComplete="off"
            maxLength={MAX_NOTE}
            placeholder="Mei's ramen crawl video"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      )}
      {offline && <p className="caption tm-muted tm-save-link-note">No signal: saving needs signal.</p>}
      {error && (
        <p className="caption tm-error tm-save-link-note" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
      {url.trim() && (
        <span className="tm-save-link-actions">
          <Button variant="primary" size="sm" type="submit" disabled={busy || offline}>
            {busy ? "Saving…" : "Save"}
          </Button>
          <Button
            variant="quiet"
            size="sm"
            type="button"
            onClick={() => {
              setUrl("");
              setNote("");
              setProblem(null);
            }}
          >
            Cancel
          </Button>
        </span>
      )}
    </form>
  );
}
